/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { ApiDocuments, ApiFolders } from "../ApiDocuments";
import { GetSelectedLanguage } from "../../helper/Method";

/**
 * Custom hook for document and folder operations
 */
export const useDocumentHandlers = (state) => {
  const { t } = useTranslation();

  // Destructure commonly used state setters
  const {
    documents,
    setDocuments,
    selectedDocumentsForDelete,
    setSelectedDocumentsForDelete,
    setSelectedByDragging,
    setDeletionInProgress,
    setHierarchyDisable,
    setSearchTerm,
    setSelectedTags,
    setFolderId,
    setFolderUnitId,
    setUnitFolderUnitId,
    setDetailBox,
    setHoveredRowIndex,
    setSelectedRowIndex,
    folderId,
    folderUnitId,
    searchTerm,
    selectedTags,
    setLoading,
    setResultFromSearch,
    setUploadInProgress,
    setIsCalled,
    isCalled,
  } = state;

  const notify = (string) => toast.success(string);

  /**
   * Handle single file/folder deletion
   */
  const handleOnDelete = useCallback(
    async (id, type, folderContainsFiles) => {
      const isFolder = type.includes("folder");
      swal({
        title: t("common_delete_message"),
        text: t(
          isFolder && folderContainsFiles
            ? "folder_delete_detail_message"
            : "common_delete_detail_message"
        ),
        className: GetSelectedLanguage() === "en" ? "" : "ur",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then(async (willDelete) => {
        if (!willDelete) return;

        const toastId = toast.loading(
          t(
            isFolder
              ? "document_delete_folder_toast"
              : "document_delete_single_file_toast"
          ),
          {
            position: toast.POSITION.BOTTOM_LEFT,
            style: { backgroundColor: "#40576d", color: "#DFE4ED" },
          }
        );

        const updatedDocuments = documents.filter((d) => d.id !== id);
        setDocuments(updatedDocuments);
        setDeletionInProgress(true);

        const api = isFolder ? ApiFolders() : ApiDocuments();

        try {
          await api.delete(id);
          toast.update(toastId, {
            render:
              1 +
              t(
                isFolder
                  ? "document_delete_folder_toast_success"
                  : "document_delete_single_file_toast_success"
              ),
            type: "success",
            isLoading: false,
            autoClose: 3000,
            style: { backgroundColor: "#ffffff", color: "grey" },
          });
          setDeletionInProgress(false);
        } catch (err) {
          console.error(err);
          setDeletionInProgress(false);
          swal({
            title: err.response?.data,
            icon: "error",
            buttons: "OK",
          });
          toast.update(toastId, {
            render: "Error occurred",
            type: "error",
            isLoading: false,
            autoClose: 3000,
            style: { backgroundColor: "#ffffff", color: "grey" },
          });

          uploadRefreshDocuments();
        } finally {
          setLoading(false);
        }
      });
    },
    [documents, t, setDocuments, setDeletionInProgress, setLoading]
  );

  /**
   * Handle multiple file/folder deletion
   */
  const handleMultipleDelete = useCallback(() => {
    const containsFolder = selectedDocumentsForDelete.some((item) =>
      item.fileType.includes("folder")
    );

    swal({
      title: t("common_delete_message"),
      text: t(
        containsFolder
          ? "folder_delete_detail_message"
          : "common_delete_detail_message"
      ),
      className: GetSelectedLanguage() === "en" ? "" : "ur",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        performMultipleDelete();
      }
    });
  }, [selectedDocumentsForDelete, t]);

  /**
   * Perform the actual multiple deletion
   */
  const performMultipleDelete = useCallback(() => {
    let tempValues = [...documents];
    const updatedDocuments = documents.filter(
      (d) => !selectedDocumentsForDelete.some((file) => file.id === d.id)
    );
    setDocuments(updatedDocuments);

    const toastId = toast.loading(t("document_delete_file_toast"), {
      position: toast.POSITION.BOTTOM_LEFT,
      style: { backgroundColor: "#40576d", color: "#DFE4ED" },
    });

    setDeletionInProgress(true);

    ApiDocuments()
      .deleteMultiple(
        selectedDocumentsForDelete.map(({ id, fileType }) => ({
          id,
          type: fileType,
        }))
      )
      .then((res) => {
        toast.update(toastId, {
          render:
            selectedDocumentsForDelete.length +
            t("document_delete_file_toast_success"),
          type: "success",
          isLoading: false,
          autoClose: 3000,
          style: { backgroundColor: "#ffffff", color: "grey" },
        });
        setSelectedDocumentsForDelete([]);
        setSelectedByDragging(false);
        setDeletionInProgress(false);
      })
      .catch((err) => {
        console.error(err);
        setDeletionInProgress(false);
        setDocuments(tempValues);
        toast.update(toastId, {
          render: "Error occurred",
          type: "error",
          isLoading: false,
          autoClose: 3000,
          style: { backgroundColor: "#ffffff", color: "grey" },
        });
        swal({
          title: err.response?.data || "Error occurred",
          icon: "error",
          buttons: "OK",
        });
        setSelectedDocumentsForDelete([]);
        setSelectedByDragging(false);
        uploadRefreshDocuments();
      });
  }, [documents, selectedDocumentsForDelete, t]);

  /**
   * Handle file download
   */
  const handleDownload = useCallback(
    async (fileId, index) => {
      state.setIsDownloading((prevStates) => {
        const newLoadingStates = [...prevStates];
        newLoadingStates[index] = true;
        return newLoadingStates;
      });

      try {
        const response = await ApiDocuments().downloadDocument(fileId);

        if (response.status === 200) {
          const modifiedLink = `${response.data.downloadLink}&confirm=t`;
          window.location.href = modifiedLink;
        } else {
          swal({
            title: t(response.data),
            icon: "error",
            buttons: "OK",
          });
          console.error("Error getting download link:", response.data);
        }
      } catch (error) {
        swal({
          title: error.message,
          icon: "error",
          buttons: "OK",
        });
      } finally {
        state.setIsDownloading((prevStates) => {
          const newLoadingStates = [...prevStates];
          newLoadingStates[index] = false;
          return newLoadingStates;
        });
      }
    },
    [t]
  );

  /**
   * Refresh documents list
   */
  const uploadRefreshDocuments = useCallback(() => {
    if (isCalled) return;

    setIsCalled(true);
    setHierarchyDisable(true);

    ApiDocuments()
      .getDocuments({
        folderUnitId,
        folderId,
        searchString: searchTerm,
        tags: selectedTags,
      })
      .then((res) => {
        handleData(res);
        setHierarchyDisable(false);
        setSelectedRowIndex(null);
        setUploadInProgress(false);
        setHoveredRowIndex(null);
        setResultFromSearch(res.data.resultFromSearch);
        setSelectedDocumentsForDelete([]);
        setSelectedByDragging(false);
        setDetailBox(false);
      })
      .catch((err) => {
        console.log(err);
        setHierarchyDisable(false);
        swal({
          title: err.message || "Error occurred",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setIsCalled(false);
      });
  }, [
    isCalled,
    folderUnitId,
    folderId,
    searchTerm,
    selectedTags,
    setIsCalled,
    setHierarchyDisable,
    setSelectedRowIndex,
    setUploadInProgress,
    setHoveredRowIndex,
    setResultFromSearch,
    setSelectedDocumentsForDelete,
    setSelectedByDragging,
    setDetailBox,
  ]);

  /**
   * Handle breadcrumb clicks for folder navigation
   */
  const handleBreadCrumbs = useCallback(
    (item) => {
      if (!state.loading) {
        setSearchTerm("");
        setSelectedTags([]);
        setFolderId(item.folderId);
        setFolderUnitId(item.folderUnitId ? item.folderUnitId : folderUnitId);
        if (item.folderUnitId) setUnitFolderUnitId(item.folderUnitId);
      }
    },
    [
      state.loading,
      folderUnitId,
      setSearchTerm,
      setSelectedTags,
      setFolderId,
      setFolderUnitId,
      setUnitFolderUnitId,
    ]
  );

  /**
   * Handle split of documents into regular and unit folder documents
   */
  const handleData = useCallback(
    (res) => {
      const files = res.data.files;
      const documentsData = files.filter((file) => file.folderUnitId === null);
      const unitFoldersData = files.filter((file) => file.folderUnitId !== null);

      state.setDocuments(documentsData);
      state.setUnitFolders(unitFoldersData);
      if (unitFoldersData.length === 0) {
        state.setActiveTab("otherFiles");
      }
    },
    [state]
  );

  /**
   * Copy link to clipboard
   */
  const copyToClipboard = useCallback(
    (link) => {
      navigator.clipboard
        .writeText(link)
        .then(() => {
          notify(t("document_link_copied"));
        })
        .catch((err) => {
          notify("Failed to copy: " + err);
        });
    },
    [t]
  );

  /**
   * Remove file from multiple selection
   */
  const removeIdFromSelectedRows = useCallback(
    (id) => {
      setSelectedDocumentsForDelete((prevSelectedRows) => {
        const updatedArray = prevSelectedRows.filter((row) => row.id !== id);
        return updatedArray;
      });
      setSelectedByDragging(false);
    },
    [setSelectedDocumentsForDelete, setSelectedByDragging]
  );

  return {
    handleOnDelete,
    handleMultipleDelete,
    performMultipleDelete,
    handleDownload,
    uploadRefreshDocuments,
    handleBreadCrumbs,
    handleData,
    copyToClipboard,
    removeIdFromSelectedRows,
  };
};
